import React, { useState, useEffect, useRef } from 'react';
import { shape, PropTypes } from 'prop-types';
import moment from 'moment';
import Modal from '../Modal/ReactBootstrapModal';
import { paymentIconsDictionary } from '../../utils/constants';
import paymentsConstants from '../../../constants/payments';
import Img from '../Img';
import './cards.scss';

const useOutsideClickEvent = (ref, callBack) => {
    useEffect(() => {
        function handleClickOutside(event) {
            if (ref.current && !ref.current.includes(event.target)) {
                callBack(true);
            } else {
                callBack(false);
            }
        }
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [ref]);
};

const PaymentMethodList = (props) => {
    const { cardList,
        handleAddNewCard,
        deleteAddedCard,
        enableAction,
        changeDefaultCard,
        selectedPaymentMethod,
        changePaymentMethod,
        selectPaymentMethod,
        showDefaultCardPaymentPage,
        stripe,
        submitting,
        currencySymbol,
        eventFee,
        walletPaymentButton,
        isUsedForPayment,
        buttonText,
        isSubmitting,
        formType,
        disabled,
    } = props;

    const { deleteCardMessages } = paymentsConstants;

    const [state, setState] = useState({
        showModal: false,
        selectedCard: null,
        modalText: { header: '', body: '' },
        cardAction: null,
        openCardAction: '',
    });
    const [userSelectedCard, setUserSelectedCard] = useState(null);

    /**
     * sort the saved cards list
     * in order to show default card at top
     * by Neeraj dated:- 22 july 22
     */
    const [orderedSavedCards, setSavedCardsOrder] = useState(cardList);

    const getSortedCardList = cardsArray => {
        return cardsArray.sort((card1, card2) => {
            return card2.isDefaultPaymentMethod ? 1 : -1;
        });
    };

    const getDefaultCardIndex = cardsArr => (cardsArr.findIndex(card => card.isDefaultPaymentMethod));

    useEffect(() => {
        const sortedCardsList = cardList?.length > 1 &&
            getDefaultCardIndex(cardList) !== -1
            ? getSortedCardList(cardList)
            : cardList;
        setSavedCardsOrder(sortedCardsList);

        const defaultCardIndex = getDefaultCardIndex(sortedCardsList);
        if (defaultCardIndex !== -1) {
            const defaultCard = cardList[defaultCardIndex];
            setUserSelectedCard(defaultCard);
        }
    }, [cardList]);

    const cardRef = useRef([]);

    const hideActionMenu = (clicked) => {
        if (clicked) {
            setState((prevState) => ({
                ...prevState,
                openCardAction: '',
            }));
        }
    };

    useOutsideClickEvent(cardRef, hideActionMenu);
    const { showModal, modalText, openCardAction, cardAction, selectedCard } = state;

    const confirmDeleteCard = (card) => {
        const deleteNote = card.isUsedInSubscription ? deleteCardMessages.deleteNote : '';
        setState((prevState) => ({
            ...prevState,
            showModal: true,
            selectedCard: card.id,
            modalText: { header: deleteCardMessages.header, body: deleteCardMessages.body, deleteNote },
            cardAction: 'delete',
        }));
    };

    const confirmChangeDefault = async (cardId) => {
        if (cardId) {
            await changeDefaultCard(cardId);
        }
    };

    const handleModalClose = () => {
        setState((prevState) => ({
            ...prevState,
            showModal: false,
            selectedCard: null,
            modalText: { header: '', body: '' },
            cardAction: null,
        }));
    };

    const handleConfirmButton = async () => {
        setState((prevState) => ({
            ...prevState,
            showModal: false,
        }));
        if (cardAction === 'delete') {
            await deleteAddedCard(selectedCard);
        }
        setState((prevState) => ({
            ...prevState,
            selectedCard: null,
            modalText: { header: '', body: '' },
            cardAction: null,
        }));
    };

    const handleOpenAction = (cardId) => {
        setState((prevState) => ({
            ...prevState,
            openCardAction: cardId,
        }));
    };

    const assignRef = (ref, index) => {
        cardRef.current[index] = ref;
    };

    const handleCardChange = (card) => {
        if (!submitting && !card.isExpired) {
            setUserSelectedCard(card);
            selectPaymentMethod(card);
        }
    };

    const renderCardExpiryDate = (card) => {
        const labelToShow = card.isExpired ? 'Expired' : 'Exp';
        const expiryDate = moment(`${card.card.expMonth}-01-${card.card.expYear}`, 'MM-DD-YYY').format('MM/YY');
        return `${labelToShow} ${expiryDate}`;
    };

    const checkIfSavedCardDoesNotExists = () => !selectedPaymentMethod && showDefaultCardPaymentPage && walletPaymentButton;
    return (
        <>
            <div className="payment-wrap">
                {/* page subtitles */}

                {showDefaultCardPaymentPage
                    ? (
                        <>
                            <div className="card-title text-center event-payment-title">Almost there! How would you like to pay?</div>
                            {eventFee && <div className="total-payment">TOTAL PAYMENT:  {currencySymbol}{eventFee > 0 && eventFee.toFixed(2)}</div>}
                        </>
                    )
                    : isUsedForPayment ? <div className="event-payment-title card-title">Select a saved payment option below or add a new one.</div> : ''}

                {/* render wallet payment button */}
                {!!walletPaymentButton && walletPaymentButton()}

                {/* submit button when using default card screen */}
                {(selectedPaymentMethod && showDefaultCardPaymentPage) && (
                    <>
                        <button
                            type="submit"
                            className={`btn btn-xl btn-primary text-uppercase font-weight-normal w-100 mb-4 ${!stripe || submitting ? 'text-dark' : ''}`}
                            disabled={!stripe || submitting}
                        >
                            {submitting && (<i className="fas fa-circle-notch fa-spin" />)} {selectedPaymentMethod.card.brand} **** {selectedPaymentMethod.card.last4}
                        </button>
                        <button type="button" onClick={changePaymentMethod} className="btn btn-xl btn-white font-weight-normal w-100">Select or add another payment option</button>
                    </>
                )}

                {checkIfSavedCardDoesNotExists() && (
                    <button type="button" onClick={handleAddNewCard} className="btn btn-xl btn-white font-weight-normal w-100">Pay with a new credit/debit card</button>
                )}

                {/* show cards added in profile */}
                {(cardList?.length > 0 && !showDefaultCardPaymentPage) && (
                    <div className="payment-card shadow-light">
                        {!isUsedForPayment && (
                            <div className="card-title">
                                Saved Payment Methods
                            </div>
                        )}
                        <div className="card-list">
                            {
                                orderedSavedCards?.length && orderedSavedCards.map((card, index) => {
                                    return (
                                        isUsedForPayment
                                            ? (
                                                <div key={card.id} className="radio-button radio-button-xl item d-flex justify-content-between align-items-center">
                                                    <div className="left-part d-flex justify-content-start align-items-center">
                                                        <input
                                                            id={`division${card.id}`}
                                                            type="checkbox"
                                                            checked={!!userSelectedCard && userSelectedCard.id === card.id && !userSelectedCard.isExpired}
                                                            onChange={() => handleCardChange(card)}
                                                        />
                                                        <label htmlFor={`division${card.id}`}>
                                                            <div className="radio-button-check">
                                                                {!!userSelectedCard && userSelectedCard.id === card.id && <Img src="/img/icons/checkmark-icon.svg" />}
                                                            </div>
                                                        </label>
                                                        <div className="c-icon">
                                                            <img src={`/img/payment/${paymentIconsDictionary[card.card.brand]}`} className="img-fluid" alt="" />
                                                        </div>
                                                        <div className="c-info">
                                                            <div className="name">{card.card.brand} **** {card.card.last4}</div>
                                                            <div className="date">{renderCardExpiryDate(card)}</div>
                                                        </div>
                                                    </div>
                                                    <div className="right-part d-flex justify-content-end align-items-center">
                                                        <div className="d-flex justify-content-end align-items-center">
                                                            {card.isDefaultPaymentMethod && (
                                                                <div className="default">
                                                                    <span className="badge badge-secondary">Default</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                            : (
                                                <div onClick={() => selectPaymentMethod(card)} key={card.id} className="item d-flex justify-content-between align-items-center">
                                                    <div className="left-part d-flex justify-content-start align-items-center">
                                                        <div className="c-icon">
                                                            <img src={`/img/payment/${paymentIconsDictionary[card.card.brand]}`} className="img-fluid" alt="" />
                                                        </div>
                                                        <div className="c-info">
                                                            <div className="name">{card.card.brand} **** {card.card.last4}</div>
                                                            <div className="date">{renderCardExpiryDate(card)}</div>
                                                        </div>
                                                    </div>
                                                    <div className="right-part d-flex justify-content-end align-items-center">
                                                        <div className="d-flex justify-content-end align-items-center">
                                                            {card.isDefaultPaymentMethod && (
                                                                <div className="default">
                                                                    <img src="/img/blue-checked.svg" className="img-fluid" alt="" />
                                                                </div>
                                                            )}
                                                            {enableAction && (
                                                                <div className="action">
                                                                    <span>
                                                                        <img ref={(ref) => assignRef(ref, index)} onClick={() => handleOpenAction(card.id)} src="/img/more-vert.svg" className={`img-fluid ${index}`} alt="" />
                                                                    </span>
                                                                    <div className={`dd-list ${openCardAction === card.id ? '' : 'd-none'}`}>
                                                                        {!card.isExpired && (<div onClick={() => confirmChangeDefault(card.id)} className={`dd-link ${card.isDefaultPaymentMethod ? 'disabled' : ''}`}>Set Default</div>)}
                                                                        <div onClick={() => confirmDeleteCard(card)} className="dd-link">Remove</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                    );
                                })
                            }
                        </div>
                    </div>
                )}

                {/* submit payment buttons for event registration and power subscription */}
                {(isUsedForPayment && !showDefaultCardPaymentPage) && (
                    <>
                        <div className="payment-card shadow-light">
                            <div className="card-list">
                                <div onClick={handleAddNewCard} className="item add-card cursor-pointer d-flex justify-content-between align-items-center">
                                    <div className="left-part d-flex justify-content-start align-items-center">
                                        <div className="c-info">
                                            <div className="name text-transform-none">Pay with a new credit/debit card</div>
                                        </div>
                                    </div>
                                    <div className="right-part d-flex justify-content-end align-items-center">
                                        <div className="d-flex justify-content-end align-items-center">
                                            <div className="arrow">
                                                <img src="/img/right-arrow-black.png" className="img-fluid" alt="" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {formType === 'powered' || formType === 'cc_update'
                            ? (

                                <button
                                    type="submit"
                                    className="btn btn-xl btn-primary text-uppercase font-weight-normal w-100"
                                    disabled={!userSelectedCard || userSelectedCard.isExpired || submitting || disabled || isSubmitting}
                                >{currencySymbol ? 'Pay' : ''} {currencySymbol || buttonText}{eventFee > 0 && eventFee.toFixed(2)}
                                </button>

                            )
                            : (

                                <button
                                    type="submit"
                                    className={`btn btn-xl btn-primary text-uppercase font-weight-normal w-100 ${!stripe || submitting ? 'text-dark' : ''}`}
                                    disabled={!userSelectedCard || userSelectedCard.isExpired || !stripe || submitting}
                                >
                                    {submitting && (<i className="fas fa-circle-notch fa-spin" />)} Pay {currencySymbol}{eventFee > 0 && eventFee.toFixed(2)}
                                </button>

                            )}
                    </>
                )}

                {/* add new card button in profile section */}
                {(!isUsedForPayment) && (
                    <div className="payment-card">
                        <div className="card-title">
                            Add Payment Methods
                        </div>
                        <div className="card-list">
                            <div onClick={handleAddNewCard} className="item cursor-pointer d-flex justify-content-between align-items-center">
                                <div className="left-part d-flex justify-content-start align-items-center">
                                    <div className="c-icon">
                                        <img src="/img/payment/cc-img.png" className="img-fluid" alt="" />
                                    </div>
                                    <div className="c-info">
                                        <div className="name">Add Credit/Debit Card</div>
                                    </div>
                                </div>
                                <div className="right-part d-flex justify-content-end align-items-center">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <div className="arrow">
                                            <img src="/img/right-arrow-black.png" className="img-fluid" alt="" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {showModal && (
                    <Modal className="popup-xs" visible={true} fade={true} dialogClassName="modal-dialog-centered">
                        <div className="modal-body text-center">
                            <h3 className="modal-title">{modalText.header}</h3>
                            <p className="payment-text">{modalText.body}</p>
                            <p className={`payment-text ${modalText.deleteNote ? 'd-block' : 'd-none'}`}>{modalText.deleteNote}</p>
                            <button type="button" className="btn btn-block btn-primary text-uppercase" onClick={handleConfirmButton}>Yes</button>
                            <button type="button" className="btn btn-block btn-white text-uppercase" onClick={handleModalClose}>Cancel</button>
                        </div>
                    </Modal>
                )}
            </div>
        </>
    );
};

PaymentMethodList.defaultProps = {
    cardList: [],
    handleAddNewCard: () => { },
    deleteAddedCard: () => { },
    changeDefaultCard: () => { },
    changePaymentMethod: () => {},
    selectPaymentMethod: () => {},
    enableAction: false,
    selectedPaymentMethod: null,
};
PaymentMethodList.propTypes = {
    cardList: PropTypes.arrayOf(shape),
    handleAddNewCard: PropTypes.func,
    deleteAddedCard: PropTypes.func,
    changeDefaultCard: PropTypes.func,
    changePaymentMethod: PropTypes.func,
    selectPaymentMethod: PropTypes.func,
    // eslint-disable-next-line react/forbid-prop-types
    enableAction: PropTypes.any,
    // eslint-disable-next-line react/forbid-prop-types
    selectedPaymentMethod: PropTypes.any,
};
export default PaymentMethodList;
